import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCompetition } from '../contexts/CompetitionContext';
import { fetchCompetitionGames } from '../services/competitionApi';
import { PageContainer } from '../components/layout/PageContainer';
import { PageHeader } from '../components/layout/PageHeader';
import { PageLoading } from '../components/ui/PageLoading';
import { Card } from '../components/ui/Card';
import { UpcomingGameListCard } from '../components/dashboard/UpcomingGameListCard';
import toast from 'react-hot-toast';
import { Calendar, Filter, ChevronDown, Trophy } from 'lucide-react';
import dayjs from 'dayjs';

const UpcomingGames = () => {
  const { t, language, isRTL } = useLanguage();
  const { user } = useAuth();
  const { competitionSlug, basePath } = useCompetition();
  const [allGames, setAllGames] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRound, setSelectedRound] = useState('current');
  const [availableRounds, setAvailableRounds] = useState([]);
  const [showCloseGamesOnly, setShowCloseGamesOnly] = useState(true);

  const isGameClose = useCallback((game) => {
    if (!game?.date || !game?.hour) return false;
    const gameTime = dayjs(`${game.date} ${game.hour}`);
    const daysDiff = gameTime.diff(dayjs(), 'days');
    return daysDiff <= 5 && daysDiff >= 0;
  }, []);

  const getUniqueRounds = useCallback((gamesData) => {
    if (!Array.isArray(gamesData)) return [];
    return [...new Set(gamesData.map((g) => g?.round).filter(Boolean))].sort();
  }, []);

  const getCurrentRound = useCallback(
    (gamesData) => {
      if (!Array.isArray(gamesData)) return null;
      const closeGames = gamesData.filter(isGameClose);
      if (closeGames.length === 0) return null;
      const roundCounts = {};
      closeGames.forEach((game) => {
        if (game?.round) roundCounts[game.round] = (roundCounts[game.round] || 0) + 1;
      });
      const keys = Object.keys(roundCounts);
      if (keys.length === 0) return null;
      return keys.reduce((a, b) => (roundCounts[a] > roundCounts[b] ? a : b));
    },
    [isGameClose]
  );

  const filterGames = useCallback(
    (gamesData) => {
      if (!Array.isArray(gamesData)) return [];
      let filtered = gamesData;

      if (selectedRound === 'current') {
        const currentRound = getCurrentRound(gamesData);
        if (currentRound) {
          filtered = filtered.filter((g) => g?.round === currentRound);
        } else if (showCloseGamesOnly) {
          filtered = filtered.filter(isGameClose);
        }
      } else if (selectedRound !== 'all') {
        filtered = filtered.filter((g) => g?.round === selectedRound);
      }

      if (showCloseGamesOnly && selectedRound !== 'current') {
        filtered = filtered.filter(isGameClose);
      }

      return filtered;
    },
    [selectedRound, showCloseGamesOnly, getCurrentRound, isGameClose]
  );

  const fetchUpcomingGames = useCallback(async () => {
    try {
      setLoading(true);
      const gamesData = await fetchCompetitionGames(competitionSlug, 'upcoming');
      setAllGames(gamesData);

      const rounds = getUniqueRounds(gamesData);
      const roundOptions = [
        { value: 'current', label: language === 'en' ? 'Current round' : 'סיבוב נוכחי' }
      ];
      rounds.forEach((round) => roundOptions.push({ value: round, label: round }));
      roundOptions.push({ value: 'all', label: language === 'en' ? 'All rounds' : 'כל הסיבובים' });
      setAvailableRounds(roundOptions);
      setGames(filterGames(gamesData));
    } catch (error) {
      console.error('Error fetching upcoming games:', error);
      toast.error(
        language === 'en' ? 'Failed to load upcoming games' : 'נכשל בטעינת משחקים עתידיים'
      );
    } finally {
      setLoading(false);
    }
  }, [competitionSlug, language, getUniqueRounds, filterGames]);

  useEffect(() => {
    fetchUpcomingGames();
  }, [fetchUpcomingGames]);

  useEffect(() => {
    if (allGames.length > 0) {
      setGames(filterGames(allGames));
    }
  }, [allGames, filterGames]);

  const getGameStatus = (game) => {
    if (game.isBettingLocked) return { status: 'live', text: t('live') };
    return { status: 'upcoming', text: t('upcoming') };
  };

  const canUserBet = (game) => {
    if (!user || game.isBettingLocked) return false;
    return dayjs(`${game.date} ${game.hour}`).isAfter(dayjs());
  };

  const activeFilterLabel = () => {
    if (selectedRound === 'current' && !showCloseGamesOnly) {
      return language === 'en' ? 'Current round (all)' : 'סיבוב נוכחי (הכל)';
    }
    if (selectedRound === 'all') {
      return language === 'en' ? 'All rounds' : 'כל הסיבובים';
    }
    return availableRounds.find((r) => r.value === selectedRound)?.label || selectedRound;
  };

  if (loading) {
    return (
      <PageLoading
        message={language === 'en' ? 'Loading upcoming games…' : 'טוען משחקים עתידיים…'}
      />
    );
  }

  return (
    <PageContainer dir={isRTL ? 'rtl' : 'ltr'}>
      <PageHeader
        title={language === 'en' ? 'Upcoming games' : 'משחקים עתידיים'}
        subtitle={
          language === 'en'
            ? `${games.length} games available for betting`
            : `${games.length} משחקים זמינים להימורים`
        }
        icon={Calendar}
        actions={
          <span className="inline-flex items-center gap-2 rounded-xl bg-brand-50 px-3 py-2 text-sm font-semibold text-brand-800 ring-1 ring-brand-200/80">
            <Trophy className="h-4 w-4" />
            {language === 'en' ? 'Place your bets' : 'הימר עכשיו'}
          </span>
        }
      />

      <Card className="mb-6" padding="p-4 sm:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-800">
              <Filter className="h-4 w-4 text-brand-600" />
              {language === 'en' ? 'Filters' : 'מסננים'}
            </span>

            <div className="relative">
              <select
                value={selectedRound}
                onChange={(e) => setSelectedRound(e.target.value)}
                className="tap-target min-w-[10rem] cursor-pointer appearance-none rounded-xl border border-zinc-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-zinc-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25"
              >
                {availableRounds.map((round) => (
                  <option key={round.value} value={round.value}>
                    {round.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-700">
              <input
                type="checkbox"
                checked={showCloseGamesOnly}
                onChange={(e) => setShowCloseGamesOnly(e.target.checked)}
                className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
              />
              {language === 'en' ? 'Close games only (5 days)' : 'משחקים קרובים בלבד (5 ימים)'}
            </label>
          </div>

          {(selectedRound !== 'current' || !showCloseGamesOnly) && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-zinc-500">
                {language === 'en' ? 'Active filter:' : 'מסנן פעיל:'}
              </span>
              <span className="rounded-md bg-brand-100 px-2 py-0.5 text-xs font-semibold text-brand-800">
                {activeFilterLabel()}
              </span>
            </div>
          )}
        </div>
      </Card>

      {games.length === 0 ? (
        <Card className="py-16 text-center">
          <Calendar className="mx-auto mb-4 h-12 w-12 text-zinc-300" />
          <p className="text-lg font-semibold text-zinc-800">
            {language === 'en' ? 'No upcoming games' : 'אין משחקים עתידיים'}
          </p>
          <p className="mt-2 text-sm text-zinc-500">
            {language === 'en'
              ? 'Try changing filters or check back later.'
              : 'נסה לשנות מסננים או חזור מאוחר יותר.'}
          </p>
        </Card>
      ) : (
        <ul className="space-y-4">
          {games.map((game) => (
            <li key={game._id}>
              <UpcomingGameListCard
                game={game}
                basePath={basePath}
                userCanBet={canUserBet(game)}
                hasUserBet={Boolean(game.userBet)}
                gameStatus={getGameStatus(game)}
              />
            </li>
          ))}
        </ul>
      )}
    </PageContainer>
  );
};

export default UpcomingGames;
